/* MANISH CUSTOM CODE - FAST DELIVERY FEE GUARD (manish-cc) - START
 *
 * Loaded on every page (see snippets/scripts.liquid). It keeps the cart honest
 * about the "Fast Delivery Fee" product:
 *
 *   - Adding a product whose form carries the hidden value _delivery_tier
 *     (set by the pincode box when Fast / Same-day is chosen):
 *       the fee line is added FIRST, then the product, so the cart drawer the
 *       theme redraws already shows both. If the product fails to add, the fee
 *       is taken back out. If the fee cannot be added, the product is NOT added
 *       and the shopper sees a message instead (so nobody gets Fast delivery
 *       without paying for it).
 *   - Removing / changing items in the cart: if no Fast product is left, the
 *       fee line is removed; if the fee line is missing, the wrong tier or has
 *       a quantity other than 1, it is corrected.
 *
 * One fee per order: the 30-minute fee wins if the cart has both kinds.
 *
 * >>> If you rename the product's handle, change FEE_HANDLE here AND
 *     mm_fee_handle in blocks/manish-cc-delivery-check.liquid. <<<
 *
 * The fee product's FIRST variant is the 30-minute fee, the SECOND is the
 * same-day fee (do not reorder them in Shopify Admin).
 */

export const FEE_HANDLE = 'fast-delivery-fee';
const FEE_PROP = '_delivery_fee';
const TIER_PROP = '_delivery_tier';

export function tierOf(item) {
  const t = item && item.properties && item.properties[TIER_PROP];
  return t === 'p30' || t === 'sd' ? t : null;
}

export function isFeeLine(item) {
  return !!(item && item.properties && item.properties[FEE_PROP]);
}

export function neededTier(items) {
  let need = null;
  for (const item of items || []) {
    if (isFeeLine(item)) continue;
    const t = tierOf(item);
    if (t === 'p30') return 'p30';
    if (t === 'sd') need = 'sd';
  }
  return need;
}

/**
 * What has to change in the cart so the fee line matches the Fast products.
 * @returns {{remove: string[], setQty: Array<{key: string, qty: number}>, add: number|null}}
 */
export function planFee(items, fee) {
  const plan = { remove: [], setQty: [], add: null };
  const need = neededTier(items);
  const lines = (items || []).filter(isFeeLine);

  if (!need) {
    lines.forEach((l) => plan.remove.push(l.key));
    return plan;
  }

  const wanted = fee && fee[need];
  if (!wanted) return plan; // cannot repair without the fee product; leave the cart alone

  let kept = null;
  for (const l of lines) {
    if (!kept && l.variant_id === wanted) {
      kept = l;
      if (l.quantity !== 1) plan.setQty.push({ key: l.key, qty: 1 });
    } else {
      plan.remove.push(l.key);
    }
  }
  if (!kept) plan.add = wanted;
  return plan;
}

export function createGuard(origFetch, root) {
  const base = root || '/';
  let feePromise = null;
  let chain = Promise.resolve();

  function serial(fn) {
    const run = chain.then(fn, fn);
    chain = run.catch(() => {});
    return run;
  }

  function json(url, body) {
    return origFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function errorResponse(message) {
    return new Response(JSON.stringify({ status: 422, message, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function getFee() {
    if (!feePromise) {
      feePromise = origFetch(base + 'products/' + FEE_HANDLE + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (!p || !p.variants || p.variants.length < 2) return null;
          const v = p.variants;
          return { p30: v[0].available ? v[0].id : null, sd: v[1].available ? v[1].id : null };
        })
        .catch(() => null)
        .then((fee) => {
          if (!fee) feePromise = null; // try again next time
          return fee;
        });
    }
    return feePromise;
  }

  async function getCart() {
    const r = await origFetch(base + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('cart');
    return r.json();
  }

  function sectionParams(init) {
    try {
      const body = typeof init.body === 'string' ? JSON.parse(init.body) : null;
      const out = {};
      if (body && body.sections) out.sections = body.sections;
      if (body && body.sections_url) out.sections_url = body.sections_url;
      return out;
    } catch (e) {
      return {};
    }
  }

  /**
   * Carries out a plan. The last cart change carries `extra` (the theme's
   * sections request) so its response can be handed back to the theme.
   * @returns {Promise<Response|null>} the last change response, or null if nothing changed
   */
  async function applyPlan(plan, extra) {
    let addedKey = null;
    if (plan.add) {
      const r = await json(base + 'cart/add.js', {
        items: [{ id: plan.add, quantity: 1, properties: { [FEE_PROP]: '1' } }],
      });
      if (!r.ok) throw new Error('fee-add');
      const j = await r.json();
      addedKey = j && j.items && j.items[0] && j.items[0].key;
    }

    const changes = [];
    plan.remove.forEach((key) => changes.push({ id: key, quantity: 0 }));
    plan.setQty.forEach((c) => changes.push({ id: c.key, quantity: c.qty }));
    if (!changes.length && addedKey) changes.push({ id: addedKey, quantity: 1 });
    if (!changes.length) return null;

    let last = null;
    for (let i = 0; i < changes.length; i++) {
      const body = Object.assign({}, changes[i]);
      if (i === changes.length - 1 && extra) Object.assign(body, extra);
      last = await json(base + 'cart/change.js', body);
    }
    return last;
  }

  async function fastAdd(input, init) {
    const tier = init.body.get('properties[' + TIER_PROP + ']');
    const fee = await getFee();
    if (!fee || !fee[tier]) {
      return errorResponse((window.mmDeliveryText && window.mmDeliveryText.unavailable) || 'Fast delivery is not available right now. Please choose regular delivery.');
    }

    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      return errorResponse('Could not update your cart. Please try again.');
    }

    // Plan against the cart as it will be once this product is in it.
    const pretend = (cart.items || []).concat([{ properties: { [TIER_PROP]: tier } }]);
    const plan = planFee(pretend, fee);
    let feeChanged = false;
    try {
      if (plan.add || plan.remove.length || plan.setQty.length) {
        await applyPlan(plan, null);
        feeChanged = true;
      }
    } catch (e) {
      return errorResponse((window.mmDeliveryText && window.mmDeliveryText.feeError) || 'Could not add the fast delivery fee. Please try again.');
    }

    const res = await origFetch(input, init);
    let ok = res.ok;
    try {
      const body = await res.clone().json();
      if (body && body.status) ok = false;
    } catch (e) {
      /* non-JSON success body is fine */
    }

    if (!ok && feeChanged) {
      try {
        const after = await getCart();
        await applyPlan(planFee(after.items, fee), null);
      } catch (e) {
        /* nothing more we can do; the automation also checks for a missing fee */
      }
    }
    return res;
  }

  async function afterChange(res, init) {
    if (!res.ok) return res;
    let cart;
    try {
      cart = await res.clone().json();
    } catch (e) {
      return res;
    }
    if (!cart || !Array.isArray(cart.items)) return res;

    const need = neededTier(cart.items);
    const hasFeeLine = cart.items.some(isFeeLine);
    if (!need && !hasFeeLine) return res; // the common case: nothing to do

    const fee = need ? await getFee() : null;
    const plan = planFee(cart.items, fee);
    if (!plan.add && !plan.remove.length && !plan.setQty.length) return res;

    try {
      const last = await applyPlan(plan, sectionParams(init));
      return last || res;
    } catch (e) {
      return res;
    }
  }

  const ADD = /\/cart\/add(\.js)?$/;
  const CHANGE = /\/cart\/(change|update|clear)(\.js)?$/;

  return function guardedFetch(input, init) {
    let path = '';
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input);
      path = new URL(url, 'https://x.invalid').pathname;
    } catch (e) {
      return origFetch(input, init);
    }

    const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return origFetch(input, init);

    if (ADD.test(path) && init && typeof FormData !== 'undefined' && init.body instanceof FormData) {
      if (init.body.get('properties[' + TIER_PROP + ']')) {
        return serial(() => fastAdd(input, init));
      }
    }

    if (CHANGE.test(path)) {
      return serial(async () => {
        const res = await origFetch(input, init);
        return afterChange(res, init || {});
      });
    }

    return origFetch(input, init);
  };
}

if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.__mmDeliveryGuard) {
  window.__mmDeliveryGuard = true;
  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  window.fetch = createGuard(window.fetch.bind(window), rootUrl);
}

/* MANISH CUSTOM CODE - FAST DELIVERY FEE GUARD (manish-cc) - END */
